import Skeleton from '@mui/material/Skeleton';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { DocumentationTranslationKeys } from 'intl/keys';
import { DocumentationFields } from 'components/dataCreationForms/withDocumentationForm/types';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { MemoStatefulMultiselect } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import { DocumentationFile, isSavedDocumentationFile } from 'store/documentation/model/types';
import { useLevels } from 'store/level';
import {
  useLevelsToSelect,
} from 'store/level';
import { MemoPreviewField } from 'components/general/PreviewField';
import { MultiChoiceFormFieldProps } from 'components/inspection/Form/types';
import { isSupportedAsMap } from 'shared/domain/documentation/availableExtensionsAsMap';
import { findDocumentationOnLevelsAndSites } from 'shared/domain/documentation/findDocumentationOnLevels';
import {
  ImageConvertStatus,
  ImageConvertStatusNegative,
} from 'shared/domain/imageConvertStatus/imageConvertStatus';
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { LabelledEntity } from 'shared/types/commonView';
import { getConvertInfo, getStatusIcon, getStatusTextId } from './model';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import { LevelModel } from 'shared/domain/level/types/model';
import { sortByProp } from 'helpers/sort';
import { closeAfterFirstThenStayOpenStrategy } from 'components/common/simpleInputFields/common';
import { PreselectedMapContext } from 'components/dataCreationForms/level/types';

export function DocumentationInformationForm({
  file,
}: {
  file: DocumentationFile;
}): ReactElement {
  const intl = useIntl();
  const { loading: loadingLevels, items: levels } = useLevels();
  const { loading: loadingLevelsToSelect, levels: levelsToSelect } =
    useLevelsToSelect();
  const showLevelsSkeleton =
    (loadingLevels || loadingLevelsToSelect) && levelsToSelect.length === 0;
  const { setValues, values } = useInputForm();

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const formValuesGetter = useCallback(
    () => ({
      values: valuesRef.current,
      preselectedMap: isSavedDocumentationFile(file)
        ? ({
            documentationId: file.documentationId,
            versionId: file.versionId,
            localId: file.localId,
            name: file.title,
          } satisfies PreselectedMapContext)
        : undefined,
    }),
    [file]
  );

  const documentationId = file.documentationId;
  const versionId = file.versionId;

  const { levelsValue } = useMemo(
    () =>
      findDocumentationOnLevelsAndSites(
        levels,
        [],
        documentationId,
        versionId
      ),
    [levels, documentationId, versionId]
  );

  const levelsFieldProps: MultiChoiceFormFieldProps<DocumentationFields> =
    useMemo(
      () => ({
        formKey: 'levels',
        required: false,
        labelId: DocumentationTranslationKeys.useAsMap,
        fieldName: 'multiselect-levels',
        available: sortByProp(
          levelsToSelect.reduce<LabelledEntity[]>(
            (available: LabelledEntity[], level: LevelModel) => {
              if (!level.deleted) {
                available.push({
                  _id: level._id,
                  label: `${level.label}`,
                  group: level?.siteLabel ?? '',
                });
              }
              return available;
            },
            []
          ),
          'group'
        ) as LabelledEntity[],
        groupBy: (option: LabelledEntity) => option.group || '',
        defaultValue: levelsValue,
        entityName: EditableStandardEntityName.level,
        reserveSpaceForHelperText: false,
        dense: true,
        'data-qa': 'documentations_levels',
        issueParams: null,
        popupControlStrategy: closeAfterFirstThenStayOpenStrategy,
        formValuesGetter,
      }),
      [levelsToSelect, levelsValue, formValuesGetter]
    );

  // Keeps the form's levels in line with the levels from the store, without marking the form edited.
  useEffect(() => {
    setValues('levels', levelsValue, {
      skipValidation: true,
      skipFormEdit: true,
    });
  }, [setValues, levelsValue]);

  const extension = file.data?.extension || '';
  const isSupported = isSupportedAsMap(extension, file.imageConvertStatus);

  return (
    <>
      <div className='row'>
        <LevelsMultiselectOrInfo
          remoteId={versionId}
          extension={extension}
          isSupported={isSupported}
          loading={showLevelsSkeleton}
          file={file}
          levelsFieldProps={levelsFieldProps}
        />
      </div>
      <div className='row'>
        <MemoPreviewField
          label={formatTranslation(intl, DocumentationTranslationKeys.creationDate)}
          disabled={true}
          value={file.createdAt}
          dense={true}
        />
      </div>
      <div className='row'>
        <MemoPreviewField
          label={formatTranslation(intl, DocumentationTranslationKeys.author)}
          disabled={true}
          value={file.createdBy?.label || ' '}
          dense={false}
        />
      </div>
    </>
  );
}

function UnsupportedFileWarning({
  status,
  extension,
}: {
  status: ImageConvertStatusNegative | undefined | null;
  extension: string;
}): ReactElement {
  const intl = useIntl();
  const convertedInfo = getConvertInfo(status);
  const statusTextId = getStatusTextId(status);
  const Icon = getStatusIcon(status);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        color: `rgb(113,113,113)`,
        fontSize: '0.8em',
      }}
    >
      <Icon />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: '8px',
        }}
      >
        {Boolean(convertedInfo) && (
          <span>
            {formatTranslation(intl, unsafeTranslationKey(convertedInfo) , { extension })}
          </span>
        )}
        <span>{formatTranslation(intl, unsafeTranslationKey(statusTextId) )}</span>
      </div>
    </div>
  );
}

function LevelsMultiselectOrInfo({
  remoteId,
  extension,
  levelsFieldProps,
  file,
  isSupported,
  loading,
}: {
  remoteId: string | undefined;
  extension: string;
  levelsFieldProps: MultiChoiceFormFieldProps<DocumentationFields>;
  file: DocumentationFile;
  isSupported: boolean;
  loading: boolean;
}): ReactElement {
  if (!remoteId) {
    return (
      <UnsupportedFileWarning
        extension={extension}
        status={ImageConvertStatus.PENDING}
      />
    );
  }

  if (!isSupported) {
    return (
      <UnsupportedFileWarning
        extension={extension}
        status={file.imageConvertStatus as ImageConvertStatusNegative}
      />
    );
  }

  return loading ? (
    <div className='row'>
      <Skeleton variant='text' width={'100%'} height={100} />
    </div>
  ) : (
    <MemoStatefulMultiselect {...levelsFieldProps} />
  );
}
